import { AxiosError } from 'axios';
import { t } from 'i18next';
import { makeAutoObservable, runInAction } from 'mobx';

import {
  type BindCardRequest,
  type BindCardResponse,
  type BindCardVerification,
  type CheckSmsKeyRequest,
  OrderRequests,
} from '~/api/Order';
import { Task } from '~/stores/shared/Task';

const POLL_INTERVAL_MS = 1000;
const MAX_POLL_ATTEMPTS = 15;
const NEW_CARD_POLL_INTERVAL_MS = 2000;
const MAX_NEW_CARD_POLL_ATTEMPTS = 40;

import type { CheckoutStore } from '../CheckoutStore/CheckoutStore';
import type { MainStore } from '../MainStore';
import type { OrderStore } from '../OrderStore/OrderStore';

/** Step of the bind-card flow shown in the modal. */
export type BindCardStep = 'form' | 'sms' | 'webview';

/**
 * Raw form values as typed by the user, with formatting characters kept
 * (spaces in PAN, slash in exp, plus/spaces in phone). Normalization happens
 * at submit time.
 */
export interface CardFormValues {
  pan: string;
  exp: string;
  cardHolderName: string;
  phone: string;
  cvc2: string;
}

const normalizePan = (s: string) => s.replace(/\s/g, '');

/**
 * Converts an MM/YY display string ("05/28") into the YYMM format expected
 * by the bind-card backend ("2805"). Returns an empty string when the input
 * does not contain exactly four digits.
 */
const normalizeExp = (s: string) => {
  const digits = s.replace(/\D/g, '');
  if (digits.length !== 4) return '';
  return digits.slice(2, 4) + digits.slice(0, 2);
};

/**
 * Normalizes a phone display string into the digits-only format expected by the
 * bind-card backend, ensuring the Uzbekistan country code "998" prefix.
 *
 * - "901234567"            → "990000000000"
 * - "990000000000"         → "990000000000"
 * - "+998 90 123 45 67"    → "990000000000"
 */
const normalizePhone = (s: string) => {
  const digits = s.replace(/\D/g, '');
  if (digits.startsWith('998')) return digits;
  return `998${digits}`;
};

const extractMessage = (error: unknown): string => {
  if (error instanceof AxiosError) {
    const status = error.response?.status;
    if (status === 404) return t('errors:bindCardSessionNotFound');
    if (status === 409) return t('errors:bindCardSessionExpired');
    const data = error.response?.data as
      | { errors?: Array<{ message?: string }>; message?: string }
      | undefined;
    if (data?.errors?.[0]?.message) return data.errors[0].message;
    if (data?.message) return data.message;
    return error.message;
  }
  if (error instanceof Error) return error.message;
  return t('errors:oops');
};

export class BindCardStore {
  step: BindCardStep = 'form';
  error: string | null = null;

  bindingUuid: string | null = null;
  verification: BindCardVerification | null = null;
  secondsLeft = 0;

  lastFormValues: CardFormValues | null = null;
  redirectUrl: string | null = null;
  isPolling = false;
  isWaitingForCard = false;
  shouldClose = false;

  private countdownTimer: ReturnType<typeof setInterval> | null = null;
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private pollAttempts = 0;

  private knownCardIds: Set<string> = new Set();
  private newCardPollTimer: ReturnType<typeof setTimeout> | null = null;
  private newCardPollAttempts = 0;
  private inAppBrowserOpenedAt: number | null = null;

  readonly bindCardTask = new Task<
    Promise<BindCardResponse>,
    [BindCardRequest]
  >((data) => OrderRequests.bindCard(data));

  readonly checkSmsKeyTask = new Task<Promise<void>, [CheckSmsKeyRequest]>(
    (data) => OrderRequests.checkSmsKey(data),
  );

  get isLoading(): boolean {
    return (
      this.bindCardTask.isLoading ||
      this.checkSmsKeyTask.isLoading ||
      this.isPolling ||
      this.isWaitingForCard
    );
  }

  constructor(
    private readonly orderStore: OrderStore,
    private readonly checkoutStore: CheckoutStore,
    private readonly mainStore: MainStore,
  ) {
    makeAutoObservable(this, {
      bindCardTask: false,
      checkSmsKeyTask: false,
    });

    this.bindCardTask.on('success', (response) => {
      runInAction(() => {
        this.bindingUuid = response.bindingUuid;
        if (response.flow === 'hosted_otp') {
          this.redirectUrl = response.redirectUrl;
          this.step = 'webview';
        } else {
          this.startPolling(response.bindingUuid);
        }
      });
    });

    this.bindCardTask.on('error', (e) => {
      this.mainStore.pushAlert('error', extractMessage(e));
      if (e instanceof AxiosError && e.response?.status === 409) {
        this.reset();
      }
    });
  }

  setError(err: string | null) {
    this.error = err;
  }

  reset() {
    this.stopCountdown();
    this.stopPolling();
    this.stopNewCardPoll();
    this.step = 'form';
    this.error = null;
    this.bindingUuid = null;
    this.verification = null;
    this.secondsLeft = 0;
    this.lastFormValues = null;
    this.redirectUrl = null;
    this.shouldClose = false;
    this.knownCardIds = new Set();
    this.inAppBrowserOpenedAt = null;
  }

  goBackToForm() {
    this.stopCountdown();
    this.stopPolling();
    this.step = 'form';
    this.error = null;
  }

  async submitCardForm(values: CardFormValues): Promise<void> {
    this.lastFormValues = values;
    this.stopCountdown();
    this.stopPolling();
    this.stopNewCardPoll();
    this.verification = null;
    this.secondsLeft = 0;
    this.knownCardIds = new Set(this.collectOctoCardIds());
    try {
      await this.bindCardTask.run({
        pan: normalizePan(values.pan),
        exp: normalizeExp(values.exp),
        cardHolderName: values.cardHolderName.trim(),
        phone: normalizePhone(values.phone),
        cvc2: values.cvc2,
        deliveryZoneId: this.getDeliveryZoneId(),
      });
    } catch {
      // error toast handled by task.on('error')
    }
  }

  async submitSmsKey(smsKey: string): Promise<{ ok: boolean }> {
    if (!this.bindingUuid || !this.verification) return { ok: false };

    this.error = null;
    const deliveryZoneId = this.getDeliveryZoneId();
    try {
      await this.checkSmsKeyTask.run({
        bindingUuid: this.bindingUuid,
        smsKey,
        verifyId: this.verification.verifyId,
        deliveryZoneId,
      });
      void this.pollForNewCard();
      return { ok: true };
    } catch (e) {
      runInAction(() => {
        this.error = extractMessage(e);
      });
      return { ok: false };
    }
  }

  async resendCode(): Promise<void> {
    if (!this.lastFormValues) return;
    await this.submitCardForm(this.lastFormValues);
  }

  /**
   * Called from WebViewStep after the BindResult page posts a message back.
   * Starts polling methods/get until the new card appears.
   * No-op if the modal is not on the webview step.
   */
  handleHostedOtpComplete(): void {
    if (this.step !== 'webview') return;
    void this.pollForNewCard();
  }

  /**
   * Records the timestamp when WebViewStep asks the host RN app to open the
   * in-app browser. Used by `handleInAppBrowserClosed` to ignore close events
   * that fire too quickly (a synchronous RN-side `InAppBrowser.open` failure).
   */
  markInAppBrowserOpened(): void {
    this.inAppBrowserOpenedAt = Date.now();
  }

  /**
   * Reaction to an `inAppBrowserClosed` event from the host RN app. Treats it
   * as a manual user dismiss only when the in-app browser stayed open long
   * enough (>= 2s); otherwise the close is the result of a synchronous open
   * failure and we let the methods/get polling handle it.
   */
  handleInAppBrowserClosed(): void {
    if (this.step !== 'webview') return;
    if (this.inAppBrowserOpenedAt === null) return;
    if (Date.now() - this.inAppBrowserOpenedAt < 2000) return;
    this.handleHostedOtpFail();
  }

  /**
   * Called when Octo signals an unsuccessful bind-card attempt
   * (e.g. `octo_status=canceled`). Stops polling, closes the in-app browser
   * on RN, and surfaces a failure alert. No-op if the modal is not on the
   * webview step.
   */
  handleHostedOtpFail(): void {
    if (this.step !== 'webview') return;
    if (this.shouldClose) return;
    this.stopNewCardPoll();
    if (this.mainStore.isRN) {
      this.mainStore.sendToRN('closeInAppBrowser');
    }
    this.mainStore.pushAlert('error', t('errors:bindCardFailed'));
    this.shouldClose = true;
  }

  private pollForNewCard(): void {
    if (this.isWaitingForCard) return;
    this.isWaitingForCard = true;
    this.newCardPollAttempts = 0;
    void this.newCardPollTick();
  }

  private newCardPollTick = async (): Promise<void> => {
    if (!this.isWaitingForCard) return;
    this.newCardPollAttempts += 1;

    const deliveryZoneId = this.getDeliveryZoneId();
    try {
      await this.orderStore.requestPayments(deliveryZoneId);
    } catch {
      // network blip — keep polling
    }
    if (!this.isWaitingForCard) return;

    const currentIds = this.collectOctoCardIds();
    const hasNew = currentIds.some((id) => !this.knownCardIds.has(id));

    if (hasNew) {
      runInAction(() => this.finalizeNewCardPoll(true));
      return;
    }

    if (this.newCardPollAttempts >= MAX_NEW_CARD_POLL_ATTEMPTS) {
      runInAction(() => this.finalizeNewCardPoll(false));
      return;
    }

    this.newCardPollTimer = setTimeout(
      () => void this.newCardPollTick(),
      NEW_CARD_POLL_INTERVAL_MS,
    );
  };

  private stopNewCardPoll(): void {
    if (this.newCardPollTimer) {
      clearTimeout(this.newCardPollTimer);
      this.newCardPollTimer = null;
    }
    this.isWaitingForCard = false;
    this.newCardPollAttempts = 0;
  }

  private finalizeNewCardPoll(success: boolean): void {
    this.stopNewCardPoll();
    if (this.mainStore.isRN) {
      this.mainStore.sendToRN('closeInAppBrowser');
    }
    if (success) {
      this.mainStore.pushAlert('success', t('cardAdded'));
    } else {
      this.mainStore.pushAlert('error', t('errors:bindCardFailed'));
    }
    this.shouldClose = true;
  }

  private collectOctoCardIds(): string[] {
    return this.orderStore.paymentCards.map((c) => c.id);
  }

  private startCountdown() {
    this.stopCountdown();
    this.countdownTimer = setInterval(() => {
      runInAction(() => {
        if (this.secondsLeft > 0) this.secondsLeft -= 1;
        else this.stopCountdown();
      });
    }, 1000);
  }

  private stopCountdown() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  private startPolling(bindingUuid: string) {
    this.stopPolling();
    this.isPolling = true;
    this.pollAttempts = 0;
    void this.pollTick(bindingUuid);
  }

  private stopPolling() {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
    this.isPolling = false;
    this.pollAttempts = 0;
  }

  private pollTick = async (bindingUuid: string) => {
    if (this.bindingUuid !== bindingUuid) return;
    this.pollAttempts += 1;

    let result: Awaited<
      ReturnType<typeof OrderRequests.getBindCardVerification>
    >;
    try {
      result = await OrderRequests.getBindCardVerification(bindingUuid);
    } catch (e) {
      runInAction(() => {
        this.stopPolling();
        this.mainStore.pushAlert('error', extractMessage(e));
      });
      return;
    }

    if (this.bindingUuid !== bindingUuid) return;

    if (result.status === 'hosted_otp') {
      const { redirectUrl } = result;
      runInAction(() => {
        this.redirectUrl = redirectUrl;
        this.step = 'webview';
        this.stopPolling();
      });
      return;
    }

    if (result.status === 'ready') {
      const { verification } = result;
      runInAction(() => {
        this.verification = verification;
        this.secondsLeft = verification.secondsLeft;
        this.step = 'sms';
        this.stopPolling();
        this.startCountdown();
      });
      return;
    }

    if (result.status === 'expired') {
      const { verification } = result;
      runInAction(() => {
        this.verification = verification;
        this.secondsLeft = 0;
        this.step = 'sms';
        this.stopPolling();
      });
      return;
    }

    if (result.status === 'failed') {
      const errorMessage = result.error;
      runInAction(() => {
        this.stopPolling();
        this.mainStore.pushAlert('error', errorMessage);
      });
      return;
    }

    if (this.pollAttempts >= MAX_POLL_ATTEMPTS) {
      runInAction(() => {
        this.stopPolling();
        this.mainStore.pushAlert('error', t('errors:bindCardOtpTimeout'));
        this.reset();
      });
      return;
    }

    this.pollTimer = setTimeout(
      () => void this.pollTick(bindingUuid),
      POLL_INTERVAL_MS,
    );
  };

  private getDeliveryZoneId(): string | undefined {
    return this.orderStore.etaCalculation?.warehouse.zone[
      this.checkoutStore.deliveryMethod
    ]?.id;
  }
}
